import React, { useEffect, useState } from 'react';
import { Crop, FlipHorizontal, Lock, Ruler, RotateCw, Trash2, Unlock } from 'lucide-react';
import type { BoardFloorPlanData, FloorPlanStair, FloorPlanWallEnd } from '../../../types';
import { measureLengthIn } from './floorPlanDimensions';
import { catalogEntry, FLOOR_PLAN_CATALOG } from './floorPlanCatalog';
import { isStairCatalogId, retargetStair, stairKindOf, stairOf, stairRunWidth } from './floorPlanStairs';
import {
  addPerpendicular,
  applyWallLength,
  constraintsForWall,
  joinCoincident,
  missingPerpPartners,
  nearestUnjoinedEnd,
  otherConstraintWallId,
  wallEndOnConstraint,
  wallLabel,
  withWallEdit,
} from './floorPlanConstraints';
import {
  GRID_MINOR_IN,
  clampOpeningOnWall,
  formatInchesFtIn,
  itemAabb,
  normalizePlanExtents,
  parseDimensionToInches,
  resetUnderlayCrop,
  rotateUnderlay,
  scaleUnderlay,
  setItemRotation,
  setUnderlayLocked,
  setUnderlayRotation,
  underlayIsCropped,
  underlayIsLocked,
  underlayRotation,
  wallLength,
} from './floorPlanMath';
import type { FloorPlanScaleRef, FloorPlanSelection } from './floorPlanTypes';

interface FloorPlanInspectorProps {
  data: BoardFloorPlanData;
  selected: FloorPlanSelection | null;
  onChange: (next: BoardFloorPlanData) => void;
  onLiveChange?: (next: BoardFloorPlanData) => void;
  onDelete: () => void;
  onRotateItem: () => void;
  scaleRef?: FloorPlanScaleRef | null;
  onStartScaleRef?: () => void;
  onCancelScaleRef?: () => void;
  snapEnabled?: boolean;
}

const FloorPlanInspector: React.FC<FloorPlanInspectorProps> = ({
  data,
  selected,
  onChange,
  onLiveChange,
  onDelete,
  onRotateItem,
  scaleRef,
  onStartScaleRef,
  onCancelScaleRef,
  snapEnabled = true,
}) => {
  const wall = selected?.kind === 'wall' ? data.walls.find((w) => w.id === selected.id) : undefined;
  const opening =
    selected?.kind === 'opening' ? data.openings.find((o) => o.id === selected.id) : undefined;
  const item = selected?.kind === 'item' ? data.items.find((i) => i.id === selected.id) : undefined;
  const underlay =
    selected?.kind === 'underlay'
      ? (data.underlays || []).find((entry) => entry.id === selected.id)
      : undefined;
  const measure =
    selected?.kind === 'measure'
      ? (data.measures || []).find((entry) => entry.id === selected.id)
      : undefined;

  const [widthDraft, setWidthDraft] = useState('');
  const [depthDraft, setDepthDraft] = useState('');
  const [lengthDraft, setLengthDraft] = useState('');
  const [offsetDraft, setOffsetDraft] = useState('');
  const [treadsDraft, setTreadsDraft] = useState('');
  const [landTreadsDraft, setLandTreadsDraft] = useState('');
  const [runWidthDraft, setRunWidthDraft] = useState('');
  const [rotationDraft, setRotationDraft] = useState('');
  const [anchor, setAnchor] = useState<FloorPlanWallEnd>('start');

  useEffect(() => {
    if (opening) {
      setWidthDraft(formatInchesFtIn(opening.widthIn));
      setOffsetDraft(formatInchesFtIn(opening.offsetIn));
    } else if (item) {
      setWidthDraft(formatInchesFtIn(item.widthIn));
      setDepthDraft(formatInchesFtIn(item.depthIn));
      if (isStairCatalogId(item.catalogId)) {
        const stair = stairOf(item);
        setTreadsDraft(String(stair.treads ?? 8));
        setLandTreadsDraft(String(stair.landTreads ?? stair.treads ?? 8));
        setRunWidthDraft(formatInchesFtIn(stairRunWidth(item)));
      }
    } else if (underlay) {
      setWidthDraft(formatInchesFtIn(underlay.widthIn));
      const rot = underlayRotation(underlay);
      setRotationDraft(Number.isInteger(rot) ? String(rot) : rot.toFixed(1));
    } else if (wall) {
      setLengthDraft(formatInchesFtIn(wallLength(wall)));
    }
  }, [opening, item, underlay, wall]);

  useEffect(() => {
    setAnchor('start');
  }, [wall?.id]);

  if (!selected || (!wall && !opening && !item && !underlay && !measure)) return null;

  const wallConstraints = wall ? constraintsForWall(data.constraints, wall.id) : [];
  const joinStart = wall ? nearestUnjoinedEnd(data.walls, data.constraints, wall.id, 'start') : null;
  const joinEnd = wall ? nearestUnjoinedEnd(data.walls, data.constraints, wall.id, 'end') : null;
  const perpPartners = wall ? missingPerpPartners(data.walls, data.constraints, wall.id) : [];

  return (
    <aside className="fp-inspector">
      {wall && (
        <>
          <div className="fp-inspector-kicker">Wall</div>
          <div className="fp-inspector-title">{formatInchesFtIn(wallLength(wall))}</div>
          <label className="fp-field">
            Length
            <input
              value={lengthDraft}
              onChange={(e) => setLengthDraft(e.target.value)}
              onBlur={() => {
                const inches = parseDimensionToInches(lengthDraft, 'ft');
                if (!inches) {
                  setLengthDraft(formatInchesFtIn(wallLength(wall)));
                  return;
                }
                onChange(
                  normalizePlanExtents(
                    withWallEdit(
                      data,
                      applyWallLength(
                        data.walls,
                        data.constraints,
                        wall.id,
                        inches,
                        anchor,
                        snapEnabled ? GRID_MINOR_IN : 0,
                      ),
                    ),
                  ),
                );
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              }}
            />
          </label>
          <label className="fp-field">
            Grow from
            <select value={anchor} onChange={(e) => setAnchor(e.target.value as FloorPlanWallEnd)}>
              <option value="start">Start</option>
              <option value="end">End</option>
            </select>
          </label>
          <label className="fp-field">
            Thickness
            <select
              value={wall.thicknessIn}
              onChange={(e) => {
                const thicknessIn = Number(e.target.value);
                onChange({
                  ...data,
                  walls: data.walls.map((w) => (w.id === wall.id ? { ...w, thicknessIn } : w)),
                });
              }}
            >
              <option value={4}>4"</option>
              <option value={6}>6"</option>
              <option value={8}>8"</option>
            </select>
          </label>
          <div className="fp-field-note">Thickness is to scale on the drawing.</div>
          <div className="fp-constraint-head">Constraints</div>
          {wallConstraints.length === 0 && (
            <div className="fp-constraint-empty">No joints on this wall.</div>
          )}
          {wallConstraints.map((constraint) => {
            const other = data.walls.find((entry) => entry.id === otherConstraintWallId(constraint, wall.id));
            const end = wallEndOnConstraint(constraint, wall.id);
            return (
              <div key={constraint.id} className="fp-constraint">
                <div>
                  <div className="fp-constraint-kind">
                    {constraint.kind === 'perpendicular' ? 'Perpendicular' : 'Coincident'}
                  </div>
                  <div className="fp-constraint-meta">
                    {constraint.kind === 'coincident' && end ? `${end} · ` : ''}
                    {other ? wallLabel(other) : 'Missing wall'}
                  </div>
                </div>
                <button
                  type="button"
                  className="fp-constraint-remove"
                  aria-label="Remove constraint"
                  onClick={() => {
                    onChange({
                      ...data,
                      constraints: (data.constraints || []).filter((entry) => entry.id !== constraint.id),
                    });
                  }}
                >
                  Remove
                </button>
              </div>
            );
          })}
          {joinStart && (
            <button
              type="button"
              className="fp-inspector-btn"
              onClick={() => {
                const next = joinCoincident(
                  data.walls,
                  data.constraints,
                  wall.id,
                  'start',
                  joinStart,
                  snapEnabled ? GRID_MINOR_IN : 0,
                );
                onChange(normalizePlanExtents(withWallEdit(data, next.walls, next.constraints)));
              }}
            >
              Join start
            </button>
          )}
          {joinEnd && (
            <button
              type="button"
              className="fp-inspector-btn"
              onClick={() => {
                const next = joinCoincident(
                  data.walls,
                  data.constraints,
                  wall.id,
                  'end',
                  joinEnd,
                  snapEnabled ? GRID_MINOR_IN : 0,
                );
                onChange(normalizePlanExtents(withWallEdit(data, next.walls, next.constraints)));
              }}
            >
              Join end
            </button>
          )}
          {perpPartners.map((partner) => (
            <button
              key={partner.id}
              type="button"
              className="fp-inspector-btn"
              onClick={() => {
                onChange({
                  ...data,
                  constraints: addPerpendicular(data.constraints, wall.id, partner.id),
                });
              }}
            >
              Make perpendicular · {wallLabel(partner)}
            </button>
          ))}
        </>
      )}

      {opening && (
        <>
          <div className="fp-inspector-kicker">
            {opening.kind === 'garage_door' ? 'Garage door' : opening.kind === 'window' ? 'Window' : 'Door'}
          </div>
          <label className="fp-field">
            Width
            <input
              value={widthDraft}
              onChange={(e) => setWidthDraft(e.target.value)}
              onBlur={() => {
                const inches = parseDimensionToInches(widthDraft, 'in');
                if (!inches) return;
                onChange({
                  ...data,
                  openings: data.openings.map((o) =>
                    o.id === opening.id ? { ...o, widthIn: Math.max(12, inches) } : o,
                  ),
                });
              }}
            />
          </label>
          <label className="fp-field">
            Offset from wall start
            <input
              value={offsetDraft}
              onChange={(e) => setOffsetDraft(e.target.value)}
              onBlur={() => {
                const inches = parseDimensionToInches(offsetDraft, 'ft');
                const host = data.walls.find((entry) => entry.id === opening.wallId);
                if (inches == null || !host) {
                  setOffsetDraft(formatInchesFtIn(opening.offsetIn));
                  return;
                }
                const sized = clampOpeningOnWall(host, { offsetIn: inches, widthIn: opening.widthIn });
                onChange({
                  ...data,
                  openings: data.openings.map((o) =>
                    o.id === opening.id ? { ...o, offsetIn: sized.offsetIn } : o,
                  ),
                });
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              }}
            />
          </label>
          {opening.kind === 'door' && (
            <label className="fp-field">
              Swing
              <select
                value={opening.swing || 'left'}
                onChange={(e) => {
                  const swing = e.target.value as 'left' | 'right';
                  onChange({
                    ...data,
                    openings: data.openings.map((o) => (o.id === opening.id ? { ...o, swing } : o)),
                  });
                }}
              >
                <option value="left">Left</option>
                <option value="right">Right</option>
              </select>
            </label>
          )}
          {opening.kind !== 'window' && (
            <button
              type="button"
              className={`fp-inspector-btn${opening.flip ? ' is-active' : ''}`}
              onClick={() => {
                onChange({
                  ...data,
                  openings: data.openings.map((o) =>
                    o.id === opening.id ? { ...o, flip: !o.flip } : o,
                  ),
                });
              }}
            >
              <FlipHorizontal size={14} />
              Flip side
            </button>
          )}
        </>
      )}

      {item && (
        <>
          <div className="fp-inspector-kicker">
            {item.catalogId === 'north-arrow' ? 'Symbol' : isStairCatalogId(item.catalogId) ? 'Stairs' : 'Item'}
          </div>
          <div className="fp-inspector-title">{catalogEntry(item.catalogId)?.label || item.catalogId}</div>
          {isStairCatalogId(item.catalogId) && (
            <>
              <label className="fp-field">
                Type
                <select
                  value={item.catalogId}
                  onChange={(e) => {
                    onChange({
                      ...data,
                      items: data.items.map((entry) =>
                        entry.id === item.id ? retargetStair(entry, e.target.value) : entry,
                      ),
                    });
                  }}
                >
                  {FLOOR_PLAN_CATALOG.filter((entry) => entry.group === 'stairs').map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.label}
                    </option>
                  ))}
                </select>
              </label>
              {(stairKindOf(item.catalogId) === 'l' || stairKindOf(item.catalogId) === 'u') && (
                <label className="fp-field">
                  Walking width
                  <input
                    value={runWidthDraft}
                    onChange={(e) => setRunWidthDraft(e.target.value)}
                    onBlur={() => {
                      const inches = parseDimensionToInches(runWidthDraft, 'in');
                      if (!inches) {
                        setRunWidthDraft(formatInchesFtIn(stairRunWidth(item)));
                        return;
                      }
                      onChange({
                        ...data,
                        items: data.items.map((entry) =>
                          entry.id === item.id
                            ? { ...entry, stair: { ...stairOf(entry), runWidthIn: Math.max(18, inches) } }
                            : entry,
                        ),
                      });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                    }}
                  />
                </label>
              )}
            </>
          )}
          {item.catalogId === 'north-arrow' || stairKindOf(item.catalogId) === 'spiral' ? (
            <label className="fp-field">
              {stairKindOf(item.catalogId) === 'spiral' ? 'Diameter' : 'Size'}
              <input
                value={widthDraft}
                onChange={(e) => setWidthDraft(e.target.value)}
                onBlur={() => {
                  const inches = parseDimensionToInches(widthDraft, stairKindOf(item.catalogId) === 'spiral' ? 'ft' : 'in');
                  if (!inches) return;
                  const size = Math.max(stairKindOf(item.catalogId) === 'spiral' ? 36 : 12, inches);
                  const next = { ...item, widthIn: size, depthIn: size };
                  const old = itemAabb(item);
                  const neu = itemAabb(next);
                  onChange({
                    ...data,
                    items: data.items.map((i) =>
                      i.id === item.id
                        ? { ...next, x: old.x + old.w / 2 - neu.w / 2, y: old.y + old.h / 2 - neu.h / 2 }
                        : i,
                    ),
                  });
                }}
              />
            </label>
          ) : (
            <>
          <label className="fp-field">
            {isStairCatalogId(item.catalogId) ? 'Width' : 'Width (in)'}
            <input
              value={widthDraft}
              onChange={(e) => setWidthDraft(e.target.value)}
              onBlur={() => {
                const inches = parseDimensionToInches(widthDraft, isStairCatalogId(item.catalogId) ? 'ft' : 'in');
                if (!inches) return;
                onChange({
                  ...data,
                  items: data.items.map((i) => (i.id === item.id ? { ...i, widthIn: Math.max(6, inches) } : i)),
                });
              }}
            />
          </label>
          <label className="fp-field">
            {stairKindOf(item.catalogId) === 'straight' ? 'Run' : isStairCatalogId(item.catalogId) ? 'Depth' : 'Depth (in)'}
            <input
              value={depthDraft}
              onChange={(e) => setDepthDraft(e.target.value)}
              onBlur={() => {
                const inches = parseDimensionToInches(depthDraft, isStairCatalogId(item.catalogId) ? 'ft' : 'in');
                if (!inches) return;
                onChange({
                  ...data,
                  items: data.items.map((i) => (i.id === item.id ? { ...i, depthIn: Math.max(6, inches) } : i)),
                });
              }}
            />
          </label>
            </>
          )}
          {isStairCatalogId(item.catalogId) && (
            <>
              <label className="fp-field">
                {stairKindOf(item.catalogId) === 'l' || stairKindOf(item.catalogId) === 'u' ? 'First run treads' : 'Treads'}
                <input
                  value={treadsDraft}
                  onChange={(e) => setTreadsDraft(e.target.value)}
                  onBlur={() => {
                    const n = Math.round(Number(treadsDraft));
                    if (!Number.isFinite(n) || n < 3) {
                      setTreadsDraft(String(stairOf(item).treads ?? 8));
                      return;
                    }
                    onChange({
                      ...data,
                      items: data.items.map((entry) =>
                        entry.id === item.id ? { ...entry, stair: { ...stairOf(entry), treads: n } } : entry,
                      ),
                    });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                  }}
                />
              </label>
              {(stairKindOf(item.catalogId) === 'l' || stairKindOf(item.catalogId) === 'u') && (
                <label className="fp-field">
                  Second run treads
                  <input
                    value={landTreadsDraft}
                    onChange={(e) => setLandTreadsDraft(e.target.value)}
                    onBlur={() => {
                      const n = Math.round(Number(landTreadsDraft));
                      if (!Number.isFinite(n) || n < 3) {
                        setLandTreadsDraft(String(stairOf(item).landTreads ?? 8));
                        return;
                      }
                      onChange({
                        ...data,
                        items: data.items.map((entry) =>
                          entry.id === item.id ? { ...entry, stair: { ...stairOf(entry), landTreads: n } } : entry,
                        ),
                      });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                    }}
                  />
                </label>
              )}
              <label className="fp-field">
                Turn
                <select
                  value={stairOf(item).turn || 'right'}
                  onChange={(e) => {
                    const turn = e.target.value as NonNullable<FloorPlanStair['turn']>;
                    onChange({
                      ...data,
                      items: data.items.map((entry) =>
                        entry.id === item.id ? { ...entry, stair: { ...stairOf(entry), turn } } : entry,
                      ),
                    });
                  }}
                >
                  <option value="right">Right</option>
                  <option value="left">Left</option>
                </select>
              </label>
            </>
          )}
          {item.catalogId === 'north-arrow' && (
            <>
              <label className="fp-field">
                Rotation
                <span className="fp-opacity-row">
                  <input
                    type="range"
                    min={0}
                    max={360}
                    step={0.5}
                    value={item.rotation || 0}
                    onChange={(e) => {
                      const rotation = Number(e.target.value);
                      const next = {
                        ...data,
                        items: data.items.map((entry) =>
                          entry.id === item.id ? setItemRotation(entry, rotation, snapEnabled) : entry,
                        ),
                      };
                      (onLiveChange || onChange)(next);
                    }}
                  />
                  <span>{Math.round(item.rotation || 0)}°</span>
                </span>
              </label>
              <div className="fp-field-note">Arrow points north. Rotate to match the site.</div>
            </>
          )}
          <button type="button" className="fp-inspector-btn" onClick={onRotateItem}>
            <RotateCw size={14} /> Rotate 90°
          </button>
        </>
      )}

      {underlay && (
        <>
          <div className="fp-inspector-kicker">Image</div>
          <div className="fp-inspector-title">{underlay.label || 'Image'}</div>
          <button
            type="button"
            className={`fp-inspector-btn${underlayIsLocked(underlay) ? ' is-active' : ''}`}
            aria-pressed={underlayIsLocked(underlay)}
            onClick={() => {
              const nextLocked = !underlayIsLocked(underlay);
              if (nextLocked) onCancelScaleRef?.();
              onChange({
                ...data,
                underlays: (data.underlays || []).map((entry) =>
                  entry.id === underlay.id ? setUnderlayLocked(entry, nextLocked) : entry,
                ),
              });
            }}
          >
            {underlayIsLocked(underlay) ? <Unlock size={14} /> : <Lock size={14} />}
            {underlayIsLocked(underlay) ? 'Unlock image' : 'Lock image'}
          </button>
          {underlayIsLocked(underlay) && (
            <div className="fp-field-note">Locked — unlock to move, crop, or scale.</div>
          )}
          <label className="fp-field">
            Scale (width)
            <input
              value={widthDraft}
              disabled={underlayIsLocked(underlay)}
              onChange={(e) => setWidthDraft(e.target.value)}
              onBlur={() => {
                const inches = parseDimensionToInches(widthDraft, 'ft');
                if (!inches) return;
                onChange({
                  ...data,
                  underlays: (data.underlays || []).map((entry) =>
                    entry.id === underlay.id ? scaleUnderlay(entry, inches) : entry,
                  ),
                });
              }}
            />
          </label>
          <label className="fp-field">
            Rotation
            <span className="fp-opacity-row">
              <input
                type="range"
                min={0}
                max={360}
                step={0.5}
                disabled={underlayIsLocked(underlay)}
                value={underlayRotation(underlay)}
                onChange={(e) => {
                  const rotation = Number(e.target.value);
                  const next = {
                    ...data,
                    underlays: (data.underlays || []).map((entry) =>
                      entry.id === underlay.id
                        ? setUnderlayRotation(entry, rotation, snapEnabled)
                        : entry,
                    ),
                  };
                  (onLiveChange || onChange)(next);
                }}
              />
              <input
                className="fp-rotation-input"
                value={rotationDraft}
                disabled={underlayIsLocked(underlay)}
                aria-label="Rotation degrees"
                onChange={(e) => setRotationDraft(e.target.value)}
                onBlur={() => {
                  const parsed = Number.parseFloat(rotationDraft);
                  if (!Number.isFinite(parsed)) {
                    const rot = underlayRotation(underlay);
                    setRotationDraft(Number.isInteger(rot) ? String(rot) : rot.toFixed(1));
                    return;
                  }
                  onChange({
                    ...data,
                    underlays: (data.underlays || []).map((entry) =>
                      entry.id === underlay.id
                        ? setUnderlayRotation(entry, parsed, snapEnabled)
                        : entry,
                    ),
                  });
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                }}
              />
              <span>°</span>
            </span>
          </label>
          {snapEnabled && !underlayIsLocked(underlay) && (
            <div className="fp-field-note">Snaps to 0° / 90° / 180° / 270°. Hold Alt on the canvas to bypass.</div>
          )}
          <label className="fp-field">
            Opacity
            <span className="fp-opacity-row">
              <input
                type="range"
                min={5}
                max={100}
                step={1}
                value={Math.round(underlay.opacity * 100)}
                onChange={(e) => {
                  const opacity = Number(e.target.value) / 100;
                  const next = {
                    ...data,
                    underlays: (data.underlays || []).map((entry) =>
                      entry.id === underlay.id ? { ...entry, opacity } : entry,
                    ),
                  };
                  (onLiveChange || onChange)(next);
                }}
              />
              <span>{Math.round(underlay.opacity * 100)}%</span>
            </span>
          </label>
          <button
            type="button"
            className="fp-inspector-btn"
            disabled={underlayIsLocked(underlay)}
            onClick={() => {
              onChange({
                ...data,
                underlays: (data.underlays || []).map((entry) =>
                  entry.id === underlay.id ? rotateUnderlay(entry) : entry,
                ),
              });
            }}
          >
            <RotateCw size={14} /> Rotate 90°
          </button>
          {!underlayIsLocked(underlay) && (
            <div className="fp-field-note">Drag the edge handles to crop the trace.</div>
          )}
          {underlayIsCropped(underlay) && (
            <button
              type="button"
              className="fp-inspector-btn"
              disabled={underlayIsLocked(underlay)}
              onClick={() => {
                onChange({
                  ...data,
                  underlays: (data.underlays || []).map((entry) =>
                    entry.id === underlay.id ? resetUnderlayCrop(entry) : entry,
                  ),
                });
              }}
            >
              <Crop size={14} /> Reset crop
            </button>
          )}
          {scaleRef?.underlayId === underlay.id ? (
            <button type="button" className="fp-inspector-btn" onClick={onCancelScaleRef}>
              Cancel two-point scale
            </button>
          ) : (
            <button
              type="button"
              className="fp-inspector-btn"
              disabled={underlayIsLocked(underlay)}
              onClick={onStartScaleRef}
            >
              <Ruler size={14} /> Scale from two points
            </button>
          )}
        </>
      )}

      {measure && (
        <>
          <div className="fp-inspector-kicker">Measure</div>
          <div className="fp-inspector-title">{formatInchesFtIn(measureLengthIn(measure))}</div>
          <div className="fp-field-note">Straight-line distance on the plan. Click Dim and two points on the model to add another.</div>
        </>
      )}

      <button type="button" className="fp-inspector-btn is-danger" onClick={onDelete}>
        <Trash2 size={14} /> Delete
      </button>
    </aside>
  );
};

export default FloorPlanInspector;
